// Global Instructions Rule Applied!
//
// Performance reviews section of the statement run form
// (docs/QUARTERLY_REPORT_DISTRIBUTION_DESIGN.md d.1 "The investor performance
// report (run-level, per account)", PR 4).
//
// The Investor Performance Report - the per-investor gross performance deck
// - is about one investor account across ALL of its funds, so it is a
// run-level document of the run, one per account that holds a commitment in
// any fund of the run, not a statement of a fund. Generate reports on the
// whole run (the Funds section's Generate all / Generate all fund reports)
// makes them after the funds' documents; this section is where they are
// reviewed: per deck its status, the stored PDF (View, Download), Generate
// again (after issue: the next version, "- v2") and Delete.
//
// Mark reports as issued on any fund issues the decks with the fund
// reports (the Issued column); a reports send carries each account's deck
// once, as its first attachment (the Sent column, as the Statements
// section's). "Out of date" marks a deck whose LP Period Highlights set has
// moved since it was generated - the comparison rides on the run read, so
// there is no "Checking" state here. A deck that failed says why on its
// row ("Generate failed" tries those again); a deck still generating when
// the server stopped reads Stalled.
//
// The Statements section is the pattern (the same columns, Tags, buttons
// and header actions; no fund filter: a deck belongs to no fund).

import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Button, Popconfirm, Space, Table, Tag, Tooltip, Typography } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { getSignedUrl } from '../../../core/services/filesService.ts'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { addTab } from '../../../store/slices/tabSlice'
import { saveBlob } from '../../document_reports/services/documentReportsService.ts'
import { deleteAccountDocument } from '../services/investorStatementsService.ts'
import type { GenerateTarget } from '../services/investorStatementsService.ts'
import { downloadStoredFiles, fetchStoredFile } from '../utils/storedStatements.ts'
import {
  AccountDocumentState,
  InvestorStatementAccountDocument,
  InvestorStatementRun,
  StatementSendState,
  StatementStatusCode
} from '../../../state_manager/data_models/investor_statement_run.ts'
import type { RunDescriptionWarning } from '../../../state_manager/data_models/investor_statement_run.ts'
import { changedPartsText, DATE_FORMAT, fileSizeText, RUN_TABLE_SCROLL } from '../utils/runDisplay.ts'
import { DescriptionWarningTags } from './DescriptionWarningTags.tsx'

const { Text } = Typography

const EMAIL_MODULE = 'email'
const RECORD_LINK_CLASS =
  'cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'

interface RunAccountDocumentsSectionProps {
  run: InvestorStatementRun
  /** Queue a generation; the hook re-reads the run. */
  generate: (target?: GenerateTarget) => Promise<{ count: number | null; accountDocuments?: number; error?: string }>
  generating: boolean
  /** Re-read the run (after a deck was deleted). */
  refetch: () => Promise<void>
  /** The unreviewed or changed company short descriptions a stored deck
   *  printed, per account document id (useRunDescriptionWarnings). */
  descriptionWarnings: Record<string, RunDescriptionWarning[]>
}

/** The stored PDF of a deck, when it has one. */
export const storedDeckFile = (row: InvestorStatementAccountDocument): { path: string; name: string } | null =>
  row.file_urls_investor_statement_account_documents__file_url
    ? {
        path: row.file_urls_investor_statement_account_documents__file_url,
        name:
          row.file_urls_investor_statement_account_documents__file_name ||
          `${row.investor_statement_types__label} - ${row.accounts__name}.pdf`
      }
    : null

const isPending = (row: InvestorStatementAccountDocument) => row.prog__state === AccountDocumentState.pending
const isFailed = (row: InvestorStatementAccountDocument) =>
  row.investor_statement_statuses__code === StatementStatusCode.failed

/** The header Tags: what the stored decks of the run add up to. */
export function accountDocumentSummaryTags(
  run: InvestorStatementRun
): Array<{ key: string; color?: string; text: string }> {
  const s = run.prog__account_document_summary
  const tags: Array<{ key: string; color?: string; text: string }> = []
  if (s.total === 0) return tags
  if (s.generated > 0) tags.push({ key: 'generated', color: 'green', text: `${s.generated} of ${s.total} generated` })
  if (s.pending > 0) tags.push({ key: 'pending', color: 'blue', text: `${s.pending} generating` })
  if (s.failed > 0) tags.push({ key: 'failed', color: 'red', text: `${s.failed} failed` })
  if (s.stalled > 0) tags.push({ key: 'stalled', color: 'gold', text: `${s.stalled} stalled` })
  if (s.file_size_bytes > 0) tags.push({ key: 'size', text: fileSizeText(s.file_size_bytes) })
  return tags
}

export default function RunAccountDocumentsSection({
  run,
  generate,
  generating,
  refetch,
  descriptionWarnings
}: RunAccountDocumentsSectionProps) {
  const dispatch = useDispatch()
  const { openFormInTab } = useTabManagerContext()
  const [busyRow, setBusyRow] = useState<string | null>(null)
  const [busyAll, setBusyAll] = useState(false)
  const [downloadingAll, setDownloadingAll] = useState(false)
  const type = run.run.prog__account_document_type
  const rows = run.account_documents
  const summary = run.prog__account_document_summary
  const isAdmin = run.run.prog__is_app_admin
  // what the sentences call them: 'performance review(s)'
  const word = (type?.investor_statement_types__short_label ?? 'Performance review').toLowerCase()
  const words = `${word}s`

  const openEmail = useCallback(
    (id: string, name?: string | null) => openFormInTab(EMAIL_MODULE, id, name ?? null),
    [openFormInTab]
  )

  const failed = useMemo(() => rows.filter(isFailed), [rows])
  const downloadable = useMemo(() => rows.filter((row) => storedDeckFile(row)), [rows])
  const staleCount = useMemo(() => rows.filter((row) => row.prog__is_stale).length, [rows])
  const sentCount = useMemo(() => rows.filter((row) => row.prog__send_state === StatementSendState.sent).length, [rows])
  // a deck being generated takes no second request
  const anyPending = summary.pending > 0

  /** The stored PDF, in a file-viewer tab (the house way to open a file). */
  const handleView = useCallback(
    async (row: InvestorStatementAccountDocument) => {
      const file = storedDeckFile(row)
      if (!file) return
      try {
        const signed = await getSignedUrl(file.path, getAccessToken(store.getState().auth))
        const tabKey = `file-viewer-${file.path.replace(/[^a-zA-Z0-9]/g, '-')}-${Date.now()}`
        dispatch(
          addTab({
            id: tabKey,
            key: tabKey,
            title: file.name,
            path: `/file-viewer/${tabKey}`,
            type: 'file-viewer',
            module: 'file-viewer',
            closeable: true,
            fileUrl: signed.signed_url,
            fileName: file.name
          })
        )
      } catch (e) {
        showErrorToast(e instanceof Error ? e.message : `Failed to open the ${word}`)
      }
    },
    [dispatch, word]
  )

  const handleDownload = useCallback(
    async (row: InvestorStatementAccountDocument) => {
      const file = storedDeckFile(row)
      if (!file) return
      setBusyRow(row.investor_statement_account_documents__id)
      try {
        saveBlob(await fetchStoredFile(file.path), file.name)
      } catch (e) {
        showErrorToast(e instanceof Error ? e.message : `Failed to download ${file.name}`)
      } finally {
        setBusyRow(null)
      }
    },
    []
  )

  /** Make the given decks again, in one request. */
  const regenerate = useCallback(
    async (targets: InvestorStatementAccountDocument[]): Promise<boolean> => {
      const res = await generate({ accountDocumentIds: targets.map((t) => t.investor_statement_account_documents__id) })
      if (res.error || res.count == null) {
        showErrorToast(res.error || `Failed to generate the ${words}`)
        return false
      }
      toast.success(`Generating ${targets.length} ${targets.length === 1 ? word : words} again`)
      return true
    },
    [generate, word, words]
  )

  const handleRegenerateOne = useCallback(
    async (row: InvestorStatementAccountDocument) => {
      setBusyRow(row.investor_statement_account_documents__id)
      try {
        await regenerate([row])
      } finally {
        setBusyRow(null)
      }
    },
    [regenerate]
  )

  const handleRegenerateMany = useCallback(
    async (targets: InvestorStatementAccountDocument[]) => {
      setBusyAll(true)
      try {
        await regenerate(targets)
      } finally {
        setBusyAll(false)
      }
    },
    [regenerate]
  )

  const handleDelete = useCallback(
    async (row: InvestorStatementAccountDocument) => {
      setBusyRow(row.investor_statement_account_documents__id)
      try {
        const res = await deleteAccountDocument(
          row.investor_statement_account_documents__id,
          getAccessToken(store.getState().auth)
        )
        if (!res.success) {
          showErrorToast(res.error || `Failed to delete the ${word} of ${row.accounts__name}`)
          return
        }
        if (res.filesNotRemoved > 0) {
          showErrorToast(`The ${word} of ${row.accounts__name} was deleted, but its file could not be moved to the trash.`)
        } else {
          toast.success(`The ${word} of ${row.accounts__name} was deleted`)
        }
        await refetch()
      } finally {
        setBusyRow(null)
      }
    },
    [refetch, word]
  )

  const handleDownloadAll = useCallback(async () => {
    setDownloadingAll(true)
    try {
      const res = await downloadStoredFiles(
        downloadable.map((row) => ({ ...(storedDeckFile(row) as { path: string; name: string }), label: row.accounts__name })),
        `Performance reviews - ${run.run.investor_statement_runs__name} - ${run.run.prog__period_label}`
      )
      if (res.saved === 0) showErrorToast(`None of the ${words} could be downloaded`)
      else if (res.failed.length > 0) showErrorToast(`Not in the download: ${res.failed.join(', ')}`)
    } finally {
      setDownloadingAll(false)
    }
  }, [downloadable, run.run, words])

  const statusCell = useCallback(
    (row: InvestorStatementAccountDocument) => {
      const code = row.investor_statement_statuses__code
      return (
        <div>
          <Space size={4} wrap>
            {row.prog__is_stalled ? (
              <Tooltip title={`Generation did not finish: the server stopped while this ${word} was being produced. Generate it again.`}>
                <Tag color='gold'>Stalled</Tag>
              </Tooltip>
            ) : (
              <Tag color={row.investor_statement_statuses__tag_color ?? undefined}>
                {row.investor_statement_statuses__label ?? code ?? '—'}
              </Tag>
            )}
            {row.investor_statement_account_documents__version > 1 ? (
              <Tooltip title={`This ${word} replaces one that was issued: its file is named "- v${row.investor_statement_account_documents__version}".`}>
                <Tag color='blue'>Revised · v{row.investor_statement_account_documents__version}</Tag>
              </Tooltip>
            ) : null}
            {code === StatementStatusCode.generated && row.prog__is_stale ? (
              <Tooltip title={`${changedPartsText(row.prog__changed_parts)} Generate it again to replace it.`}>
                <Tag color='gold'>Out of date</Tag>
              </Tooltip>
            ) : null}
            {code === StatementStatusCode.generated ? (
              <DescriptionWarningTags items={descriptionWarnings[row.investor_statement_account_documents__id]} />
            ) : null}
          </Space>
          {code === StatementStatusCode.failed && row.investor_statement_account_documents__error_message ? (
            <div className='text-xs text-red-600 mt-1'>{row.investor_statement_account_documents__error_message}</div>
          ) : null}
        </div>
      )
    },
    [word, descriptionWarnings]
  )

  const sentCell = useCallback(
    (row: InvestorStatementAccountDocument) => {
      const state = row.prog__send_state
      if (!state) return <Text type='secondary'>—</Text>
      const tag =
        state === StatementSendState.sent ? (
          <Tag color='green'>Sent{row.prog__sent_at ? ` ${dayjs(row.prog__sent_at).format(DATE_FORMAT)}` : ''}</Tag>
        ) : state === StatementSendState.failed ? (
          <Tooltip title="The email finished without this investor's message going out. Open the email to see why.">
            <Tag color='red'>Send failed</Tag>
          </Tooltip>
        ) : state === StatementSendState.draft ? (
          <Tag color='blue'>In draft email</Tag>
        ) : (
          <Tag color='blue'>Sending</Tag>
        )
      const campaignId = row.email_campaigns__id
      return (
        <div>
          {tag}
          {campaignId ? (
            <div>
              <button
                type='button'
                className={`${RECORD_LINK_CLASS} text-xs`}
                onClick={() => openEmail(campaignId, row.email_campaigns__name)}
              >
                {row.email_campaigns__name || 'Statement email'}
              </button>
            </div>
          ) : null}
        </div>
      )
    },
    [openEmail]
  )

  const columns = useMemo(
    (): ColumnsType<InvestorStatementAccountDocument> => [
      { title: 'Investor', key: 'investor', render: (_: unknown, row) => row.accounts__name },
      {
        title: 'Document',
        key: 'document',
        width: 200,
        render: (_: unknown, row) => row.investor_statement_types__short_label
      },
      { title: 'Status', key: 'status', render: (_: unknown, row) => statusCell(row) },
      {
        title: 'Pages',
        key: 'pages',
        width: 70,
        align: 'right',
        render: (_: unknown, row) => row.investor_statement_account_documents__page_count ?? '—'
      },
      {
        title: 'Size',
        key: 'size',
        width: 90,
        align: 'right',
        render: (_: unknown, row) => fileSizeText(row.file_urls_investor_statement_account_documents__file_size_bytes)
      },
      {
        title: 'Generated',
        key: 'generated',
        width: 110,
        render: (_: unknown, row) =>
          row.investor_statement_account_documents__generated_at
            ? dayjs(row.investor_statement_account_documents__generated_at).format(DATE_FORMAT)
            : '—'
      },
      {
        title: 'Issued',
        key: 'issued',
        width: 110,
        render: (_: unknown, row) =>
          row.investor_statement_account_documents__issued_at
            ? dayjs(row.investor_statement_account_documents__issued_at).format(DATE_FORMAT)
            : '—'
      },
      { title: 'Sent', key: 'sent', width: 140, render: (_: unknown, row) => sentCell(row) },
      {
        title: '',
        key: 'actions',
        width: 360,
        align: 'right',
        render: (_: unknown, row) => {
          const file = storedDeckFile(row)
          const id = row.investor_statement_account_documents__id
          const busy = busyRow === id
          const issued = !!row.investor_statement_account_documents__issued_at
          const pending = isPending(row)
          // an issued deck goes only by an application administrator (the
          // server's rule: stmt__account_document_delete)
          const deleteLocked = pending
            ? `This ${word} is being generated.`
            : issued && !isAdmin
              ? `This ${word} was issued: only an application administrator can delete it.`
              : row.prog__send_state === StatementSendState.queued || row.prog__send_state === StatementSendState.sending
                ? 'A statement email carrying it is on its way.'
                : null
          const again = (
            <Button
              size='small'
              loading={busy && generating}
              disabled={anyPending || (generating && !busy)}
              onClick={issued ? undefined : () => void handleRegenerateOne(row)}
            >
              Generate again
            </Button>
          )
          return (
            <Space size={4}>
              {file ? (
                <Button size='small' onClick={() => void handleView(row)}>
                  View
                </Button>
              ) : null}
              {file ? (
                <Button size='small' loading={busy && !generating} onClick={() => void handleDownload(row)}>
                  Download
                </Button>
              ) : null}
              {pending ? null : (
                <Tooltip title={anyPending ? `The ${words} of the run are being generated.` : undefined}>
                  <span>
                    {issued ? (
                      <Popconfirm
                        title={`Generate this issued ${word} again?`}
                        description={
                          <div className='max-w-sm'>
                            It was issued: the new one is version {row.investor_statement_account_documents__version + 1},
                            named &quot;- v{row.investor_statement_account_documents__version + 1}&quot;. The issued file is
                            kept.
                          </div>
                        }
                        okText='Generate again'
                        disabled={anyPending || generating}
                        onConfirm={() => handleRegenerateOne(row)}
                      >
                        {again}
                      </Popconfirm>
                    ) : (
                      again
                    )}
                  </span>
                </Tooltip>
              )}
              <Popconfirm
                title={`Delete the ${word} of ${row.accounts__name}?`}
                description={
                  <div className='max-w-sm'>
                    {file ? 'Its stored file is moved to the trash. ' : ''}Generating the {words} of the run again makes it
                    anew.
                  </div>
                }
                okText='Delete'
                okButtonProps={{ danger: true }}
                disabled={deleteLocked !== null || generating}
                onConfirm={() => handleDelete(row)}
              >
                <Tooltip title={deleteLocked ?? undefined}>
                  <span>
                    <Button danger size='small' loading={busy && !generating} disabled={deleteLocked !== null || generating}>
                      Delete
                    </Button>
                  </span>
                </Tooltip>
              </Popconfirm>
            </Space>
          )
        }
      }
    ],
    [statusCell, sentCell, busyRow, generating, anyPending, isAdmin, word, words, handleView, handleDownload, handleRegenerateOne, handleDelete]
  )

  const headerActions = (
    <>
      {accountDocumentSummaryTags(run).map((tag) => (
        <Tag key={tag.key} color={tag.color}>
          {tag.text}
        </Tag>
      ))}
      {staleCount > 0 ? <Tag color='gold'>{staleCount} out of date</Tag> : null}
      {summary.issued_count > 0 ? (
        <Tag color='green'>
          {summary.issued_count === summary.total ? 'Issued' : `${summary.issued_count} of ${summary.total} issued`}
          {summary.issued_at ? ` ${dayjs(summary.issued_at).format(DATE_FORMAT)}` : ''}
        </Tag>
      ) : null}
      {summary.revised_unissued_count > 0 ? (
        <Tooltip title={`${summary.revised_unissued_count === 1 ? 'A Revised' : 'Revised'} ${summary.revised_unissued_count === 1 ? word : words} waiting to be issued: Mark reports as issued on any fund of the run issues ${summary.revised_unissued_count === 1 ? 'it' : 'them'}.`}>
          <Tag color='blue'>{summary.revised_unissued_count} revised to issue</Tag>
        </Tooltip>
      ) : null}
      {sentCount > 0 ? (
        <Tag color='green'>
          {sentCount} of {rows.length} sent
        </Tag>
      ) : null}
      {failed.length > 0 ? (
        <Button
          size='small'
          loading={busyAll}
          disabled={generating || anyPending}
          onClick={() => void handleRegenerateMany(failed)}
        >
          Generate failed
        </Button>
      ) : null}
      {downloadable.length > 0 ? (
        <Button size='small' icon={<DownloadOutlined />} loading={downloadingAll} onClick={() => void handleDownloadAll()}>
          Download all
        </Button>
      ) : null}
      {rows.length > 0 ? (
        <Popconfirm
          title={`Generate every ${word} of the run again?`}
          description={
            <div className='max-w-sm'>
              Each stored {word} is replaced by a new one made from the books and the Period Highlights as they are now.
              {summary.issued_count > 0 ? (
                <div className='mt-1'>One that was issued is replaced by a new version, named &quot;- v2&quot;.</div>
              ) : null}
            </div>
          }
          okText='Generate again'
          disabled={generating || anyPending}
          onConfirm={() => handleRegenerateMany(rows.filter((row) => !isPending(row)))}
        >
          <Tooltip title={anyPending ? `The ${words} of the run are being generated.` : undefined}>
            <span>
              <Button size='small' loading={busyAll} disabled={generating || anyPending}>
                Generate all again
              </Button>
            </span>
          </Tooltip>
        </Popconfirm>
      ) : null}
    </>
  )

  return (
    <FormSection
      title={type ? `${type.investor_statement_types__short_label}s` : 'Performance reviews'}
      titleColor='orange'
      scrollable={false}
      headerActions={headerActions}
    >
      {rows.length === 0 ? (
        <Text type='secondary'>
          Nothing is stored yet. Generate the fund reports of the whole run in the Funds section (Generate all, or
          Generate all fund reports): a {word} per investor account of the run - the investor&apos;s whole portfolio
          over the year to {run.run.prog__period_label} - is printed and stored here after the funds&apos; documents.
        </Text>
      ) : (
        <Table<InvestorStatementAccountDocument>
          size='small'
          pagination={false}
          rowKey='investor_statement_account_documents__id'
          columns={columns}
          dataSource={rows}
          scroll={RUN_TABLE_SCROLL}
          tableLayout='auto'
        />
      )}
    </FormSection>
  )
}
